import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import "./tooltip.css";

const TooltipContext = createContext({});

export const TooltipProvider = ({ children }) => {
  return <>{children}</>;
};

export const Tooltip = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef(null);
  const contentRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let x = triggerRect.left + (triggerRect.width - contentRect.width) / 2;
    let y = triggerRect.top - contentRect.height - 5;

    // Adjust x position if the tooltip goes off the edge of the viewport
    if (x < 0) {
      x = 5;
    } else if (x + contentRect.width > viewportWidth) {
      x = viewportWidth - contentRect.width - 5;
    }

    setPosition({ x, y });
  }, []);

  const value = {
    isOpen,
    setIsOpen,
    triggerRef,
    contentRef,
    position,
    calculatePosition,
  };

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
};

export const TooltipTrigger = React.forwardRef(
  ({ children, asChild = false, ...props }, ref) => {
    const { setIsOpen, triggerRef } = useContext(TooltipContext);

    return (
      <div
        {...props}
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="tooltip-trigger"
      >
        {children}
      </div>
    );
  }
);

export const TooltipContent = ({ children, className = "", ...props }) => {
  const { isOpen, contentRef, position, calculatePosition } =
    useContext(TooltipContext);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(calculatePosition, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, calculatePosition]);

  if (!isOpen) return null;

  return (
    <div
      ref={contentRef}
      className={`tooltip-content ${className}`}
      style={{
        position: "fixed",
        top: `${position.y}px`,
        left: `${position.x}px`,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export { Tooltip as default };
